import { ui } from "@/components/ui";
import type { EducationT } from "@/types/database/events";
import type { ExtendedEventViewT } from "@/types/database/events";
import { type SubmitHandler, useForm, z, zodResolver } from "@/utils/form";
import SaveButton from "../../../../components/form/SaveButton";
import EducationFormFields from "./EducationFormFields";
import { useUpdateEducationMutation } from "./api/updateEducation";

interface Props {
	diaryId: string;
	eventId: string;
	educationId: string;
	education: EducationT;
	organisationId: string;
	event: ExtendedEventViewT;
	trackerId?: string;
	navigateToNextPage?: () => void;
}

const EducationSchemaZ = z.object({
	education_name: z.string().min(1, "Education name is required"),
	education_description: z.string().min(1, "Description is required"),
	educated_by: z.string().uuid("Please select who provided the education"),
});

type EducationSchemaT = z.infer<typeof EducationSchemaZ>;

const EducationForm = ({
	education,
	organisationId,
	diaryId,
	educationId,
	navigateToNextPage,
}: Props) => {
	const educationMutation = useUpdateEducationMutation({
		diaryId: diaryId,
		educationId: educationId,
	});

	const form = useForm<EducationSchemaT>({
		resolver: zodResolver(EducationSchemaZ),
		defaultValues: {
			education_name: education?.education_name ?? "",
			education_description: education?.education_description ?? "",
			educated_by: education?.educated_by ?? undefined,
		},
	});

	const onSubmit: SubmitHandler<EducationSchemaT> = async (formData) => {
		educationMutation.mutate(
			{ ...formData, education_id: educationId },
			{
				onSuccess: () => {
					!!navigateToNextPage && navigateToNextPage();
				},
			},
		);
	};

	return (
		<ui.Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<EducationFormFields organisationId={organisationId} />
				<SaveButton isDialog={true} />
			</form>
		</ui.Form>
	);
};

export default EducationForm;
